import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Alert, Badge } from "react-bootstrap";
import axios from "axios";
import Hoot from "./Hoot";
import { toast } from "react-toastify";
import {
  BsShieldLock,
  BsKey,
  BsWallet2,
  BsCreditCard2Front,
  BsCheckCircleFill,
  BsExclamationTriangleFill,
  BsExclamationOctagonFill,
  BsDashCircle,
  BsPlayCircle,
  BsPlusLg,
  BsEyeSlash,
} from "react-icons/bs";

/**
 * VendusConfigModal
 * ------------------------------------------------------------
 * Modal para configurar a integração Vendus de uma escola:
 *  - Chave de API, ID do terminal (register_id), país
 *  - Data de expiração da licença Vendus (com atualização rápida)
 *  - Métodos de pagamento (criar, editar, remover, testar com fatura de 10 KZ)
 *
 * O botão que abre o modal é, por omissão, um botão em gradiente
 * animado infinito: azul quando tudo está OK, e passa a vermelho
 * automaticamente quando faltam <= 15 dias para a expiração (ou já
 * expirou), usando a mesma flag "isExpiringSoon" devolvida pelo backend.
 *
 * ⚠️ Zona sensível: por defeito, ao abrir o modal, os dados (chave de
 * API, terminal, métodos de pagamento) ficam ESCONDIDOS atrás de um
 * painel vermelho de aviso. Só depois de o utilizador confirmar (clicar
 * em "Mostrar dados sensíveis") é que o formulário e a lista de métodos
 * de pagamento são renderizados. Isto evita exposição acidental (ex.
 * partilha de ecrã, screenshots) de chaves e IDs de terminal.
 *
 * Props:
 *  - institute_code (obrigatório): código da escola
 *  - institute_name (opcional): nome a mostrar no cabeçalho/botão
 *  - toggle_btn (opcional): se quiseres o teu próprio botão/elemento
 *      como gatilho, passa-o aqui e o botão em gradiente não é usado.
 */
function VendusConfigModal({ institute_code, institute_name, toggle_btn }) {
  const [show, setShow] = useState(false);

  const [loading, setLoading] = useState(false); // guardar config
  const [loadingFetch, setLoadingFetch] = useState(false); // buscar dados
  const [loadingExpiration, setLoadingExpiration] = useState(false);
  const [error, setError] = useState(null);

  // ---- Gate de dados sensíveis: começa sempre fechado ----
  const [revealed, setRevealed] = useState(false);

  const [instituteInfo, setInstituteInfo] = useState(null);
  const [config, setConfig] = useState(null);
  const [daysRemaining, setDaysRemaining] = useState(null);
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const [formData, setFormData] = useState({
    api_key: "",
    register_id: "",
    country: "",
    expiration_date: "",
  });

  // ---- Métodos de pagamento: form de criação ----
  const [newMethod, setNewMethod] = useState({
    vendus_id: "",
    code: "",
    name: "",
    is_default: false,
  });
  const [savingMethod, setSavingMethod] = useState(false);

  // ---- Métodos de pagamento: edição inline ----
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({ name: "", code: "", is_default: false });
  const [savingEditId, setSavingEditId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [testingId, setTestingId] = useState(null);

  const uiStyles = `
:root{
  --primary:#5b5cff;
  --primary2:#7c3aed;
  --danger1:#ef4444;
  --danger2:#b91c1c;
  --border:#e6eaf2;
  --text:#0f172a;
  --muted:#64748b;
}

/* ===== BOTÃO GATILHO - GRADIENTE ANIMADO INFINITO ===== */
@keyframes vendusGradientShift{
  0%{ background-position: 0% 50%; }
  50%{ background-position: 100% 50%; }
  100%{ background-position: 0% 50%; }
}
@keyframes vendusPulse{
  0%{ box-shadow: 0 0 0 0 rgba(91,92,255,.45); }
  70%{ box-shadow: 0 0 0 10px rgba(91,92,255,0); }
  100%{ box-shadow: 0 0 0 0 rgba(91,92,255,0); }
}
@keyframes vendusPulseDanger{
  0%{ box-shadow: 0 0 0 0 rgba(239,68,68,.55); }
  70%{ box-shadow: 0 0 0 10px rgba(239,68,68,0); }
  100%{ box-shadow: 0 0 0 0 rgba(239,68,68,0); }
}
.vendus-trigger-btn{
  border: none;
  outline: none;
  cursor: pointer;
  color: #fff;
  font-weight: 650;
  font-size: .85rem;
  padding: 5px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center; 
  background-size: 220% 220%;
  animation: vendusGradientShift 4s ease infinite, vendusPulse 2.4s ease infinite;
  background-image: linear-gradient(120deg, #5b5cff, #7c3aed, #4338ca, #5b5cff);
  transition: transform .15s ease, filter .15s ease;
}
.vendus-trigger-btn.is-danger{
  background-image: linear-gradient(120deg, #ef4444, #b91c1c, #991b1b, #ef4444);
  animation: vendusGradientShift 4s ease infinite, vendusPulseDanger 2.4s ease infinite;
}
.vendus-trigger-btn:hover{ filter: brightness(1.06); transform: translateY(-1px); }
.vendus-trigger-btn:active{ transform: translateY(0); }
.vendus-trigger-btn:disabled{ opacity:.7; cursor: default; }

/* ===== MODAL DESIGN ===== */
.pretty-modal .modal-dialog{ max-width: 920px; }
@media (max-width: 768px){ .pretty-modal .modal-dialog{ max-width: 96vw; } }
.pretty-modal .modal-content{
  border-radius: 18px;
  border: 1px solid rgba(226,232,240,.95);
  box-shadow: 0 30px 90px rgba(2,6,23,.22);
  overflow: hidden;
}
.pretty-modal.is-danger .modal-header{
  background:
    radial-gradient(1000px 260px at 20% 0%, rgba(239,68,68,.16), transparent 60%),
    radial-gradient(900px 240px at 95% 0%, rgba(185,28,28,.12), transparent 55%),
    #ffffff;
}
.pretty-modal .modal-header{
  border-bottom: 1px solid rgba(226,232,240,.9);
  background:
    radial-gradient(1000px 260px at 20% 0%, rgba(91,92,255,.16), transparent 60%),
    radial-gradient(900px 240px at 95% 0%, rgba(124,58,237,.12), transparent 55%),
    #ffffff;
  padding: 14px 16px;
}
.pretty-modal .modal-title{ font-weight: 650; color: var(--text); font-size: 1rem; }
.pretty-modal .modal-body{
  padding: 14px 16px 12px;
  background: linear-gradient(180deg, rgba(248,250,252,.75), #ffffff 40%);
}
.body-scroll{ overflow-y: auto; padding-right: 4px; max-height: 700px; }
.pretty-modal .modal-footer{
  border-top: 1px solid rgba(226,232,240,.9);
  padding: 12px 16px;
  background: #ffffff;
}

.header-row{ display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; }
.header-left{ display:flex; align-items:center; gap:12px; }
.avatar-wrap{
  width: 44px; height: 44px; border-radius: 14px;
  background: rgba(91,92,255,.10); border: 1px solid rgba(91,92,255,.18);
  display:grid; place-items:center;
}
.avatar-wrap.is-danger{ background: rgba(239,68,68,.10); border-color: rgba(239,68,68,.2); }
.title-sub{ margin-top:2px; font-size: .8rem; color: var(--muted); font-weight: 500; }

.form-grid{ display:grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px 12px; }
@media (max-width: 720px){ .form-grid{ grid-template-columns: 1fr; } }
.cardlet{
  background: rgba(255,255,255,.9);
  border: 1px solid rgba(226,232,240,.95);
  border-radius: 16px;
  padding: 10px 12px;
}
.cardlet-title{
  font-size: .78rem; color: var(--muted); font-weight: 600;
  display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 8px;
}
.cardlet-title span.left{ display:flex; align-items:center; gap:8px; }
.req{ color:#ef4444; font-weight:700; }
.form-label{ font-size: .78rem !important; color: #475569 !important; font-weight: 600 !important; margin-bottom: 6px !important; }
.form-control, .form-select{
  border-radius: 12px !important;
  border-color: rgba(226,232,240,.95) !important;
  padding-top: .55rem !important;
  padding-bottom: .55rem !important;
}
.form-control:focus, .form-select:focus{
  box-shadow: 0 0 0 .2rem rgba(91,92,255,.15) !important;
  border-color: rgba(91,92,255,.45) !important;
}
.helper{ font-size: .74rem; color: var(--muted); margin-top: 6px; }

.status-pill{
  display:inline-flex; align-items:center; gap:6px;
  font-size:.72rem; font-weight:700;
  padding:3px 10px; border-radius:999px;
}
.status-pill.ok{ color:#15803d; background:rgba(21,128,61,.1); border:1px solid rgba(21,128,61,.2); }
.status-pill.warn{ color:#b45309; background:rgba(180,83,9,.1); border:1px solid rgba(180,83,9,.2); }
.status-pill.danger{ color:#b91c1c; background:rgba(185,28,28,.1); border:1px solid rgba(185,28,28,.2); }
.status-pill.neutral{ color:#475569; background:rgba(71,85,105,.08); border:1px solid rgba(71,85,105,.16); }

.method-row{
  display:flex; align-items:center; justify-content:space-between; gap:8px;
  padding: 8px 10px; border-radius: 12px; border:1px solid rgba(226,232,240,.95);
  background:#fff; margin-bottom:8px; flex-wrap:wrap;
}
.method-info{ display:flex; flex-direction:column; gap:2px; }
.method-name{ font-weight:650; font-size:.86rem; color:var(--text); display:flex; align-items:center; gap:6px; }
.method-meta{ font-size:.72rem; color:var(--muted); }
.method-actions{ display:flex; align-items:center; gap:6px; flex-wrap:wrap; }
.btn-soft{ border-radius: 999px !important; border: 1px solid rgba(226,232,240,.95) !important; background: #fff !important; }
.btn-primary-soft{ border-radius: 999px !important; }
.btn-xs{ font-size:.74rem !important; padding: 4px 10px !important; display:inline-flex; align-items:center; }

.actions{ display:flex; justify-content:flex-end; gap:10px; flex-wrap:wrap; width:100%; }

/* ===== PAINEL DE ZONA SENSÍVEL ===== */
.sensitive-gate{
  display:flex; flex-direction:column; align-items:center; text-align:center;
  gap:10px; padding:26px 18px;
  border: 1px solid rgba(239,68,68,.35);
  background:
    radial-gradient(600px 180px at 50% -20%, rgba(239,68,68,.10), transparent 60%),
    linear-gradient(180deg, rgba(239,68,68,.05), rgba(255,255,255,.95));
}
.sensitive-gate-icon{
  width:52px; height:52px; border-radius:16px;
  display:grid; place-items:center;
  background: rgba(239,68,68,.12);
  border: 1px solid rgba(239,68,68,.25);
  color:#b91c1c; font-size:24px;
}
.sensitive-gate-title{
  font-weight:700; color:#991b1b; font-size:.95rem;
  display:flex; align-items:center; gap:6px;
}
.sensitive-gate-text{ max-width: 460px; color:#7f1d1d; font-size:.82rem; }
`;

  const countryOptions = [
    { code: "AO", label: "Angola" },
    { code: "PT", label: "Portugal" },
    { code: "MZ", label: "Moçambique" },
    { code: "CV", label: "Cabo Verde" },
    { code: "GW", label: "Guiné-Bissau" },
    { code: "ST", label: "São Tomé e Príncipe" },
    { code: "TL", label: "Timor-Leste" },
  ];

  const fetchVendusData = useCallback(
    async ({ silent } = {}) => {
      if (!institute_code) return;
      if (!silent) setLoadingFetch(true);
      setError(null);
      try {
        const res = await axios.get(Hoot() + `admin/vendus/${institute_code}`, {
          withCredentials: true,
        });
        const data = res.data;
        setInstituteInfo(data.institute || null);
        setConfig(data.config || null);
        setDaysRemaining(
          typeof data.daysRemaining === "number" ? data.daysRemaining : null
        );
        setIsExpiringSoon(!!data.isExpiringSoon);
        setIsExpired(!!data.isExpired);
        setPaymentMethods(Array.isArray(data.paymentMethods) ? data.paymentMethods : []);

        setFormData({
          api_key: data.config?.api_key || "",
          register_id: data.config?.register_id || "",
          country: data.config?.country || "",
          expiration_date: data.config?.expiration_date
            ? String(data.config.expiration_date).slice(0, 10)
            : "",
        });
      } catch (err) {
        if (!silent) {
          const msg =
            err?.response?.data?.userMsg || "Erro ao carregar configuração Vendus.";
          setError(msg);
          toast.error(msg);
        }
        console.error(err);
      } finally {
        if (!silent) setLoadingFetch(false);
      }
    },
    [institute_code]
  );

  // Busca leve ao montar, só para decidir a cor do botão gatilho (azul/vermelho)
  useEffect(() => {
    fetchVendusData({ silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [institute_code]);

  // Busca completa sempre que o modal abre — e fecha sempre o painel sensível
  useEffect(() => {
    if (!show) return;
    fetchVendusData();
    setEditingId(null);
    setNewMethod({ vendus_id: "", code: "", name: "", is_default: false });
    setRevealed(false); // por segurança, o gate fecha-se sempre que o modal (re)abre
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const disabledAll = loading || loadingFetch;

  const closeModal = () => {
    if (loading || loadingFetch) return;
    setShow(false);
    setError(null);
    setRevealed(false);
  };

  const handleSaveConfig = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.register_id || !formData.country) {
      const msg = "O ID do terminal e o país são obrigatórios.";
      setError(msg);
      toast.error(msg);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await axios.put(
        Hoot() + `admin/vendus/${institute_code}`,
        {
          api_key: formData.api_key,
          register_id: formData.register_id,
          country: formData.country,
          expiration_date: formData.expiration_date || null,
        },
        { withCredentials: true }
      );
      toast.success(res?.data?.userMsg || "Configuração Vendus guardada!");
      fetchVendusData({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.userMsg || "Erro ao guardar configuração Vendus.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleQuickExpirationUpdate = async () => {
    if (!formData.expiration_date) {
      toast.error("Escolhe uma data de expiração primeiro.");
      return;
    }
    setLoadingExpiration(true);
    try {
      const res = await axios.patch(
        Hoot() + `admin/vendus/${institute_code}/expiration`,
        { expiration_date: formData.expiration_date },
        { withCredentials: true }
      );
      toast.success(res?.data?.userMsg || "Data de expiração atualizada!");
      setDaysRemaining(
        typeof res?.data?.daysRemaining === "number" ? res.data.daysRemaining : daysRemaining
      );
      setIsExpiringSoon(!!res?.data?.isExpiringSoon);
      fetchVendusData({ silent: true });
    } catch (err) {
      const msg =
        err?.response?.data?.userMsg || "Erro ao atualizar a data de expiração.";
      toast.error(msg);
    } finally {
      setLoadingExpiration(false);
    }
  };

  const handleAddMethod = async (e) => {
    e.preventDefault();
    if (!newMethod.vendus_id || !newMethod.name) {
      toast.error("O ID Vendus e o nome do método são obrigatórios.");
      return;
    }
    setSavingMethod(true);
    try {
      const res = await axios.post(
        Hoot() + `admin/vendus/${institute_code}/payment-methods`,
        newMethod,
        { withCredentials: true }
      );
      toast.success(res?.data?.userMsg || "Método de pagamento adicionado!");
      setNewMethod({ vendus_id: "", code: "", name: "", is_default: false });
      fetchVendusData({ silent: true });
    } catch (err) {
      const msg =
        err?.response?.data?.userMsg || "Erro ao adicionar método de pagamento.";
      toast.error(msg);
    } finally {
      setSavingMethod(false);
    }
  };

  const startEditMethod = (method) => {
    setEditingId(method.id);
    setEditData({
      name: method.name || "",
      code: method.code || "",
      is_default: !!method.is_default,
    });
  };

  const cancelEditMethod = () => {
    setEditingId(null);
    setEditData({ name: "", code: "", is_default: false });
  };

  const handleSaveEditMethod = async (id) => {
    setSavingEditId(id);
    try {
      const res = await axios.put(
        Hoot() + `admin/vendus/${institute_code}/payment-methods/${id}`,
        editData,
        { withCredentials: true }
      );
      toast.success(res?.data?.userMsg || "Método atualizado.");
      cancelEditMethod();
      fetchVendusData({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.userMsg || "Erro ao atualizar método de pagamento.";
      toast.error(msg);
    } finally {
      setSavingEditId(null);
    }
  };

  const handleDeleteMethod = async (id) => {
    setDeletingId(id);
    try {
      const res = await axios.delete(
        Hoot() + `admin/vendus/${institute_code}/payment-methods/${id}`,
        { withCredentials: true }
      );
      toast.success(res?.data?.userMsg || "Método de pagamento removido.");
      fetchVendusData({ silent: true });
    } catch (err) {
      const msg = err?.response?.data?.userMsg || "Erro ao remover método de pagamento.";
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  // Converte uma string base64 (ex: campo pdfBase64 do backend) num Blob de
  // PDF, para poder ser passado a downloadPdfBlob.
  const base64ToPdfBlob = (base64, mimeType = "application/pdf") => {
    const byteChars = atob(base64);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: mimeType });
  };

  // Descarrega um Blob de PDF diretamente (link <a download> sintético).
  //
  // Porquê não usar window.open() aqui:
  // window.open() chamado depois de um `await` (ou seja, fora do próprio
  // evento síncrono de clique) é tratado por muitos browsers como uma
  // popup NÃO iniciada pelo utilizador e é bloqueado — mas nalguns
  // browsers a chamada ainda devolve uma referência de Window não-nula,
  // o que faz o código pensar que abriu com sucesso quando na verdade
  // nada apareceu. Um download via <a download> não abre janela nenhuma,
  // por isso nunca é apanhado por bloqueadores de popups.
  const downloadPdfBlob = (blob, filename) => {
    try {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename || "fatura.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      return true;
    } catch (e) {
      console.error("[downloadPdfBlob]", e);
      return false;
    }
  };

  // Lê um Blob (JSON) devolvido pelo axios quando responseType é "blob" e
  // devolve o objeto parseado. É necessário porque com responseType "blob"
  // o axios NÃO faz parse automático de JSON — nem sequer em respostas de
  // erro (4xx/5xx), que também chegam como Blob dentro de err.response.data.
  const parseBlobAsJson = async (blob) => {
    try {
      const text = await blob.text();
      return JSON.parse(text);
    } catch (e) {
      return null;
    }
  };

  const handleTestMethod = async (method) => {
    setTestingId(method.id);
    try {
      const res = await axios.post(
        Hoot() + `admin/vendus/${institute_code}/payment-methods/${method.id}/test`,
        {},
        { withCredentials: true, responseType: "blob" }
      );

      const contentType = res.headers?.["content-type"] || "";

      // ── DEBUG: log explícito do que a resposta contém ──────────────
      // Remove isto depois de confirmares o que o backend está a devolver.
      console.log("[handleTestMethod] status:", res.status);
      console.log("[handleTestMethod] content-type:", contentType);
      console.log("[handleTestMethod] blob size (bytes):", res.data?.size);
      if (!contentType.includes("application/pdf")) {
        // se não for PDF, o blob deve ser texto/JSON — mostra o conteúdo bruto
        const rawText = await res.data.text();
        console.log("[handleTestMethod] corpo da resposta (raw):", rawText);
      }
      // ── FIM DEBUG ────────────────────────────────────────────────────

      if (contentType.includes("application/pdf")) {
        // ── PDF devolvido diretamente pelo backend: sucesso total ──────
        const downloaded = downloadPdfBlob(res.data, `teste-${method.name}.pdf`);
        if (downloaded) {
          toast.success(`Teste com "${method.name}" bem-sucedido! Fatura descarregada.`);
        } else {
          toast.error("Fatura gerada, mas o download falhou. Tenta novamente.");
        }
        return;
      }

      // ── Content-Type não é PDF: é JSON. Pode ser:
      //    (a) sucesso com o PDF embutido em base64 (formato antigo do
      //        backend, ou ?format=json), ou
      //    (b) sucesso "parcial" — documento criado mas sem PDF disponível
      //    (c) erro de validação/negócio devolvido com status 2xx
      const data = await parseBlobAsJson(res.data);

      if (data?.success && data?.pdfBase64) {
        // Backend devolveu JSON com o PDF em base64 — descarrega-o na mesma,
        // em vez de mostrar só o toast e ignorar o ficheiro.
        try {
          const pdfBlob = base64ToPdfBlob(data.pdfBase64, data.pdfMimeType);
          const downloaded = downloadPdfBlob(pdfBlob, `teste-${method.name}.pdf`);
          if (downloaded) {
            toast.success(data.userMsg || `Teste com "${method.name}" bem-sucedido!`);
          } else {
            toast.error("Fatura gerada, mas o download falhou. Tenta novamente.");
          }
        } catch (decodeErr) {
          console.error("[handleTestMethod] falha a descodificar pdfBase64", decodeErr);
          toast.error("Fatura gerada, mas o PDF veio corrompido. Tenta novamente.");
        }
        return;
      }

      if (data?.success) {
        // Sucesso sem PDF nenhum (nem base64, nem application/pdf) —
        // só resta o link "output", se vier preenchido.
        toast.success(
          data.userMsg || `Teste com "${method.name}" criado, mas sem PDF disponível.`
        );
        if (data.output) {
          window.open(data.output, "_blank", "noopener,noreferrer");
        }
        return;
      }

      toast.error(data?.userMsg || `Erro ao testar o método "${method.name}".`);
    } catch (err) {
      // Erros HTTP (4xx/5xx) também vêm como Blob por causa do
      // responseType "blob" — precisamos de os "desembrulhar" para
      // conseguir ler o userMsg real (ex: "KEY_CORRUPTED",
      // "CONFIG_NOT_FOUND", etc.) em vez de mostrar sempre uma mensagem
      // genérica.
      let msg = `Erro ao testar o método "${method.name}".`;
      if (err?.response?.data instanceof Blob) {
        const data = await parseBlobAsJson(err.response.data);
        if (data?.userMsg) msg = data.userMsg;
      } else if (err?.response?.data?.userMsg) {
        msg = err.response.data.userMsg;
      }
      console.error("[handleTestMethod]", err);
      toast.error(msg);
    } finally {
      setTestingId(null);
    }
  };

  // ---- Status visual (para pill e para o botão gatilho) ----
  const isDangerState = isExpired || isExpiringSoon;

  const statusPill = () => {
    if (!config) {
      return <span className="status-pill neutral"><BsDashCircle /> Sem configuração</span>;
    }
    if (isExpired) {
      return (
        <span className="status-pill danger">
          <BsExclamationOctagonFill />
          Expirado {daysRemaining !== null ? `há ${Math.abs(daysRemaining)} dia(s)` : ""}
        </span>
      );
    }
    if (isExpiringSoon) {
      return (
        <span className="status-pill warn">
          <BsExclamationTriangleFill />
          Expira em {daysRemaining} dia(s)
        </span>
      );
    }
    if (daysRemaining !== null) {
      return (
        <span className="status-pill ok">
          <BsCheckCircleFill />
          Ativo — expira em {daysRemaining} dia(s)
        </span>
      );
    }
    return <span className="status-pill neutral"><BsDashCircle /> Sem data de expiração</span>;
  };

  const defaultTrigger = (
    <button
      type="button"
      className={`vendus-trigger-btn${isDangerState ? " is-danger" : ""}`}
    > 
      <img style={{width:30,height:30, borderRadius:"1000px"}}  src={"https://play-lh.googleusercontent.com/DMTfEXV_HvFUGjACgR0AueEjKwbNJIaU-jqPCO63JVQ_VGp65axdsl5nzJp-RRjY3l7BxYM0JzxOanv0uqcNUMA"}  /> 
    </button>
  );

  return (
    <>
      <style>{uiStyles}</style>

      <div onClick={() => setShow(true)} style={{ display: "inline-block" }}>
        {toggle_btn || defaultTrigger}
      </div>

      <Modal
        show={show}
        onHide={closeModal}
        centered
        backdrop="static"
        keyboard={!disabledAll}
        className={`pretty-modal${isDangerState ? " is-danger" : ""}`}
      >
        <Modal.Header closeButton={!disabledAll}>
          <div className="header-row" style={{ width: "100%" }}>
            <div className="header-left">
              <div className={`avatar-wrap${isDangerState ? " is-danger" : ""}`}>
                <BsCreditCard2Front
                  style={{ color: isDangerState ? "#b91c1c" : "#5b5cff", fontSize: 18 }}
                />
              </div>
              <div>
                <div style={{ fontWeight: 650 }}>
                  Configuração Vendus{instituteInfo?.name ? ` — ${instituteInfo.name}` : ""}
                </div>
                <div className="title-sub">
                  Chave de API, terminal, expiração e métodos de pagamento.
                </div>
              </div>
            </div>
            {statusPill()}
          </div>
        </Modal.Header>

        <Modal.Body>
          <div className="body-scroll">
            {error && (
              <Alert variant="danger" className="mb-3" style={{ borderRadius: 14 }}>
                {error}
              </Alert>
            )}

            {loadingFetch ? (
              <div className="d-flex align-items-center gap-2 mb-3" style={{ color: "#64748b" }}>
                <Spinner animation="border" size="sm" /> a carregar…
              </div>
            ) : null}

            {!revealed ? (
              // ── ZONA SENSÍVEL FECHADA ──────────────────────────────────
              // A chave de API, o ID do terminal e os métodos de pagamento
              // só aparecem depois de o utilizador confirmar explicitamente.
              <div className="cardlet sensitive-gate">
                <div className="sensitive-gate-icon">
                  <BsShieldLock />
                </div>
                <div className="sensitive-gate-title">
                  <BsExclamationTriangleFill /> Zona sensível — dados confidenciais
                </div>
                <div className="sensitive-gate-text">
                  Esta secção mostra a chave de API, o ID do terminal e os métodos
                  de pagamento configurados para esta escola. Só confirmes se
                  tiveres autorização para ver e alterar estes dados.
                </div>
                <Button
                  type="button"
                  variant="danger"
                  className="btn-primary-soft"
                  onClick={() => setRevealed(true)}
                  disabled={loadingFetch}
                >
                  <BsEyeSlash className="me-2" />
                  Mostrar dados sensíveis
                </Button>
              </div>
            ) : (
              // ── ZONA SENSÍVEL ABERTA ───────────────────────────────────
              <>
                <Form onSubmit={handleSaveConfig}>
                  <div className="form-grid">
                    {/* Configuração da API */}
                    <div className="cardlet" style={{ gridColumn: "1 / -1" }}>
                      <div className="cardlet-title">
                        <span className="left"><BsKey /> Chave de API</span>
                        {config?.key_corrupted && (
                          <span className="status-pill danger">
                            <BsExclamationOctagonFill /> Chave corrompida
                          </span>
                        )}
                      </div>

                      <div className="form-grid">
                        <Form.Group>
                          <Form.Label>
                            Chave de API {!config?.has_key && <span className="req">*</span>}
                          </Form.Label>
                          <Form.Control
                            type="password"
                            placeholder={config?.has_key ? "•••••••• (preenche só para substituir)" : "Chave de API Vendus"}
                            value={formData.api_key}
                            onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                            disabled={disabledAll}
                          />
                          <div className="helper">
                            {config?.has_key
                              ? "Deixa em branco para manter a chave atual."
                              : "Obrigatória na primeira configuração desta escola."}
                          </div>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label>
                            ID do Terminal (register_id) <span className="req">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            required
                            placeholder="Ex: 12345"
                            value={formData.register_id}
                            onChange={(e) => setFormData({ ...formData, register_id: e.target.value })}
                            disabled={disabledAll}
                          />
                        </Form.Group>

                        <Form.Group>
                          <Form.Label>
                            País <span className="req">*</span>
                          </Form.Label>
                          <Form.Select
                            required
                            value={formData.country}
                            onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                            disabled={disabledAll}
                          >
                            <option value="" disabled>Selecione o país</option>
                            {countryOptions.map((c) => (
                              <option key={c.code} value={c.code}>{c.label}</option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group>
                          <Form.Label>Data de Expiração Vendus</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              type="date"
                              value={formData.expiration_date}
                              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
                              disabled={disabledAll}
                            />
                            <Button
                              type="button"
                              variant="outline-primary"
                              className="btn-soft btn-xs"
                              style={{ whiteSpace: "nowrap" }}
                              onClick={handleQuickExpirationUpdate}
                              disabled={disabledAll || loadingExpiration || !config}
                              title={!config ? "Guarda primeiro a configuração da API" : "Atualizar só a data"}
                            >
                              {loadingExpiration ? <Spinner animation="border" size="sm" /> : "Atualizar data"}
                            </Button>
                          </div>
                          <div className="helper">
                            Quando faltarem 15 dias ou menos, o botão desta escola fica vermelho automaticamente.
                          </div>
                        </Form.Group>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-end mt-3">
                    <Button type="submit" variant="danger" className="btn-primary-soft" disabled={disabledAll}>
                      {loading ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          A guardar…
                        </>
                      ) : (
                        "Guardar Configuração"
                      )}
                    </Button>
                  </div>
                </Form>

                {/* Métodos de Pagamento */}
                <div className="cardlet mt-3">
                  <div className="cardlet-title">
                    <span className="left"><BsWallet2 /> Métodos de Pagamento</span>
                    <span className="helper" style={{ margin: 0 }}>Teste cria uma fatura de 10 KZ</span>
                  </div>

                  {paymentMethods.length === 0 && !loadingFetch && (
                    <div className="helper mb-2">Ainda não há métodos de pagamento configurados.</div>
                  )}

                  {paymentMethods.map((method) => (
                    <div className="method-row" key={method.id}>
                      {editingId === method.id ? (
                        <>
                          <div className="method-info" style={{ flex: 1, minWidth: 220 }}>
                            <div className="d-flex gap-2 flex-wrap">
                              <Form.Control
                                size="sm"
                                placeholder="Nome"
                                value={editData.name}
                                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                style={{ maxWidth: 160 }}
                              />
                              <Form.Control
                                size="sm"
                                placeholder="Código (opcional)"
                                value={editData.code}
                                onChange={(e) => setEditData({ ...editData, code: e.target.value })}
                                style={{ maxWidth: 140 }}
                              />
                              <Form.Check
                                type="checkbox"
                                label="Padrão"
                                checked={editData.is_default}
                                onChange={(e) => setEditData({ ...editData, is_default: e.target.checked })}
                                className="align-self-center"
                              />
                            </div>
                          </div>
                          <div className="method-actions">
                            <Button
                              size="sm"
                              variant="primary"
                              className="btn-xs"
                              onClick={() => handleSaveEditMethod(method.id)}
                              disabled={savingEditId === method.id}
                            >
                              {savingEditId === method.id ? <Spinner animation="border" size="sm" /> : "Guardar"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              className="btn-soft btn-xs"
                              onClick={cancelEditMethod}
                              disabled={savingEditId === method.id}
                            >
                              Cancelar
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="method-info">
                            <div className="method-name">
                              {method.name}{" "}
                              {!!method.is_default && (
                                <Badge bg="" style={{ background: "rgba(91,92,255,.12)", color: "#4338ca", fontWeight: 600 }}>
                                  Padrão
                                </Badge>
                              )}
                            </div>
                            <div className="method-meta">
                              Vendus ID: {method.vendus_id}
                              {method.code ? ` · Código: ${method.code}` : ""}
                            </div>
                          </div>
                          <div className="method-actions">
                            <Button
                              size="sm"
                              variant="outline-success"
                              className="btn-soft btn-xs"
                              onClick={() => handleTestMethod(method)}
                              disabled={testingId === method.id || !config}
                              title="Cria uma fatura de teste de 10 KZ com este método"
                            >
                              {testingId === method.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <BsPlayCircle className="me-1" /> Testar (10 KZ)
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm" 
                              className="btn-soft bg-success text-dark btn-xs"
                              onClick={() => startEditMethod(method)}
                              disabled={testingId === method.id}
                            >
                             editar
                            </Button>
                            <Button
                              size="sm" 
                              className="btn-soft text-light btn-xs bg-danger"
                              onClick={() => handleDeleteMethod(method.id)}
                              disabled={deletingId === method.id}
                            >
                              {deletingId === method.id ? <Spinner animation="border" size="sm" /> : "remover"}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                  {/* Adicionar novo método */}
                  <Form onSubmit={handleAddMethod} className="mt-2">
                    <div className="d-flex gap-2 flex-wrap align-items-end">
                      <Form.Group style={{ maxWidth: 130 }}>
                        <Form.Label className="mb-1">ID Vendus <span className="req">*</span></Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ex: 2"
                          value={newMethod.vendus_id}
                          onChange={(e) => setNewMethod({ ...newMethod, vendus_id: e.target.value })}
                          disabled={savingMethod}
                        />
                      </Form.Group>
                      <Form.Group style={{ maxWidth: 140 }}>
                        <Form.Label className="mb-1">Código</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Opcional"
                          value={newMethod.code}
                          onChange={(e) => setNewMethod({ ...newMethod, code: e.target.value })}
                          disabled={savingMethod}
                        />
                      </Form.Group>
                      <Form.Group style={{ minWidth: 160, flex: 1 }}>
                        <Form.Label className="mb-1">Nome <span className="req">*</span></Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          placeholder="Ex: Transferência Bancária"
                          value={newMethod.name}
                          onChange={(e) => setNewMethod({ ...newMethod, name: e.target.value })}
                          disabled={savingMethod}
                        />
                      </Form.Group>
                      <Form.Check
                        type="checkbox"
                        label="Padrão"
                        checked={newMethod.is_default}
                        onChange={(e) => setNewMethod({ ...newMethod, is_default: e.target.checked })}
                        className="mb-2"
                        disabled={savingMethod}
                      />
                      <Button type="submit" size="sm" variant="primary" className="btn-xs mb-2" disabled={savingMethod}>
                        {savingMethod ? <Spinner animation="border" size="sm" /> : <><BsPlusLg className="me-1" /> Adicionar</>}
                      </Button>
                    </div>
                  </Form>
                </div>
              </>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <div className="actions">
            <Button
              type="button"
              variant="outline-secondary"
              className="btn-soft"
              onClick={closeModal}
              disabled={disabledAll}
            >
              Fechar
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
export default VendusConfigModal;